import React, { useState, useEffect } from 'react';
import { Storage, Auth } from 'aws-amplify';
import { useLocation } from 'react-router-dom';
import clientDB from '../config/clientDatabase';
import ActivityTracker from '../utils/activityTracker';
import './AdminUploader.css';

// Component for displaying image thumbnails
const ImageThumbnail = ({ fileKey, fileName }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadThumbnail = async () => {
      try {
        const url = await Storage.get(fileKey, {
          level: 'public',
          expires: 3600
        });
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      } finally {
        setLoading(false);
      }
    };

    loadThumbnail();
  }, [fileKey]);

  if (loading) {
    return <div className="thumbnail-loading">Loading...</div>;
  }

  return imageUrl ? (
    <img
      src={imageUrl}
      alt={fileName}
      className="file-thumbnail"
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  ) : null;
};

const AdminUploader = ({ user, company }) => {
  const location = useLocation();
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [clients, setClients] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'manage'
  const [managedFiles, setManagedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFilesToDelete, setSelectedFilesToDelete] = useState(new Set());
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    // Load clients for dropdown
    const clientCompanies = clientDB.getClientCompanies();
    setClients(clientCompanies);

    // Load recent upload history from localStorage
    const storedHistory = localStorage.getItem('uploadHistory');
    if (storedHistory) {
      try {
        setUploadHistory(JSON.parse(storedHistory));
      } catch (error) {
        setUploadHistory([]);
      }
    } else {
      setUploadHistory([]);
    }

    // Check if we're coming from client manager with pre-selected client
    if (location.state?.selectedClient) {
      setSelectedClient(location.state.selectedClient);
      setActiveTab(location.state.activeTab || 'manage');
    }
  }, [location.state]);

  // Auto-load files when client is pre-selected
  useEffect(() => {
    if (selectedClient && activeTab === 'manage') {
      loadClientFiles();
    }
  }, [selectedClient, activeTab]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const filesWithMetadata = newFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type,
      status: 'pending'
    }));

    setFiles(prevFiles => [...prevFiles, ...filesWithMetadata]);
  };

  const removeFile = (fileId) => {
    setFiles(files.filter(f => f.id !== fileId));

    // Remove from progress tracking
    const newProgress = { ...uploadProgress };
    delete newProgress[fileId];
    setUploadProgress(newProgress);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const generateFolderName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[now.getMonth()];
    return `${month} ${year}`;
  };

  const uploadFiles = async () => {
    if (!selectedClient || files.length === 0) {
      alert('Please select a client and add files to upload.');
      return;
    }

    setUploading(true);
    const folderName = selectedFolder || generateFolderName();

    try {
      for (const fileItem of files) {
        if (fileItem.status !== 'pending') continue;

        // Update file status to uploading
        setFiles(prevFiles =>
          prevFiles.map(f =>
            f.id === fileItem.id ? { ...f, status: 'uploading' } : f
          )
        );

        const key = `clients/${selectedClient}/${folderName}/${fileItem.file.name}`;

        try {
          // Verify authentication before upload
          const currentUser = await Auth.currentAuthenticatedUser();
          console.log('Authenticated user:', currentUser.username);

          // Get current credentials
          const credentials = await Auth.currentCredentials();
          console.log('Has credentials:', !!credentials);

          // Actual S3 Upload
          console.log(`Starting upload for ${fileItem.file.name} to ${key}`);
          console.log(`File type: ${fileItem.file.type}, Size: ${fileItem.file.size}`);

          const result = await Storage.put(key, fileItem.file, {
            progressCallback: (progress) => {
              const percentage = Math.round((progress.loaded / progress.total) * 100);
              setUploadProgress(prev => ({
                ...prev,
                [fileItem.id]: percentage
              }));
            },
            contentType: fileItem.file.type || 'application/octet-stream',
            level: 'public',
            metadata: {
              uploadedBy: currentUser.username,
              uploadedAt: new Date().toISOString()
            }
          });

          // Update file status to completed
          setFiles(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, status: 'completed' } : f
            )
          );

          console.log(`Successfully uploaded ${fileItem.file.name} to S3`);

          // Track upload activity
          const clientName = clients.find(c => c.id === selectedClient)?.name || selectedClient;
          const uploaderName = user?.attributes?.name || user?.username || 'Admin';
          ActivityTracker.trackFileUpload(
            fileItem.file.name,
            uploaderName,
            clientName,
            fileItem.file.size
          );
        } catch (error) {
          console.error('Error uploading file:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            name: error.name,
            stack: error.stack
          });

          // Show user-friendly error message
          alert(`Failed to upload ${fileItem.file.name}: ${error.message || 'Unknown error'}`);


          // Update file status to error
          setFiles(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, status: 'error' } : f
            )
          );
        }

        // Add to upload history
        const newHistoryItem = {
          id: Date.now() + Math.random(),
          client: clients.find(c => c.id === selectedClient)?.name || selectedClient,
          folder: folderName,
          fileName: fileItem.file.name,
          size: fileItem.size,
          uploadedAt: new Date(),
          status: 'completed'
        };

        setUploadHistory(prev => {
          const updated = [newHistoryItem, ...prev.slice(0, 9)];
          // Save to localStorage
          localStorage.setItem('uploadHistory', JSON.stringify(updated));
          return updated;
        });

        // Small delay between files
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Upload process error:', error);
    } finally {
      setUploading(false);
    }
  };

  const simulateUpload = async (fileId) => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15 + 5; // Random progress between 5-20% per step

        if (progress >= 100) {
          progress = 100;
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: progress
          }));
          clearInterval(interval);
          resolve();
        } else {
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: Math.round(progress)
          }));
        }
      }, 200); // Update every 200ms
    });
  };

  const clearCompleted = () => {
    setFiles(files.filter(f => f.status !== 'completed'));
    setUploadProgress({});
  };

  const loadClientFiles = async () => {
    if (!selectedClient) {
      alert('Please select a client first');
      return;
    }

    setLoadingFiles(true);
    try {
      const clientPath = `clients/${selectedClient}/`;

      // Load files from public level
      const result = await Storage.list(clientPath, {
        level: 'public',
        pageSize: 1000
      });

      const files = (result.results || result || []).filter(
        item => item.key && !item.key.endsWith('/')
      );

      // Organize files by folder
      const organizedFiles = files.map(file => {
        const pathParts = file.key.replace(clientPath, '').split('/');
        const fileName = pathParts[pathParts.length - 1];
        const isImage = /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileName);
        return {
          key: file.key,
          name: fileName,
          folder: pathParts.length > 1 ? pathParts[0] : 'Root',
          size: file.size || 0,
          lastModified: file.lastModified,
          selected: false,
          isImage
        };
      });

      setManagedFiles(organizedFiles);
      setSelectedFilesToDelete(new Set());
    } catch (error) {
      console.error('Error loading files:', error);
      alert('Failed to load files. Please try again.');
    } finally {
      setLoadingFiles(false);
    }
  };

  const toggleFileSelection = (fileKey) => {
    const newSelected = new Set(selectedFilesToDelete);
    if (newSelected.has(fileKey)) {
      newSelected.delete(fileKey);
    } else {
      newSelected.add(fileKey);
    }
    setSelectedFilesToDelete(newSelected);
  };

  const downloadFile = async (fileKey, fileName) => {
    try {
      const signedUrl = await Storage.get(fileKey, {
        level: 'public',
        download: true
      });

      // Create a temporary link and click it
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Track activity
      ActivityTracker.trackActivity('file_download', {
        fileName,
        client: selectedClient,
        admin: user?.attributes?.email
      });
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const getImageThumbnail = async (fileKey) => {
    try {
      const url = await Storage.get(fileKey, {
        level: 'public',
        expires: 3600 // URL expires in 1 hour
      });
      return url;
    } catch (error) {
      console.error('Error getting thumbnail:', error);
      return null;
    }
  };

  const deleteSelectedFiles = async () => {
    if (selectedFilesToDelete.size === 0) {
      alert('Please select files to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedFilesToDelete.size} file(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      for (const fileKey of selectedFilesToDelete) {
        await Storage.remove(fileKey, {
          level: 'public'
        });
      }

      alert(`Successfully deleted ${selectedFilesToDelete.size} file(s)`);

      // Reload the file list
      await loadClientFiles();
    } catch (error) {
      console.error('Error deleting files:', error);
      alert('Failed to delete some files. Please try again.');
    }
  };

  const generatePublicLink = async (fileKey, fileName) => {
    try {
      console.log('Generating public link for:', fileKey);

      // Verify user is authenticated
      const currentUser = await Auth.currentAuthenticatedUser();
      console.log('Current user:', currentUser.username);

      // Generate clean CloudFront URL (permanent, no expiration)
      // Note: fileKey from Storage.list doesn't include 'public/' prefix, so we add it
      const cloudFrontDomain = 'reports.1209digital.com';
      const fullPath = fileKey.startsWith('public/') ? fileKey : `public/${fileKey}`;
      const publicUrl = `https://${cloudFrontDomain}/${fullPath}`;

      console.log('Generated CloudFront URL:', publicUrl);

      // Copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicUrl);
        console.log('Link copied to clipboard');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = publicUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('Link copied to clipboard (fallback method)');
      }

      // Show success feedback
      setCopiedLink(fileKey);
      setTimeout(() => setCopiedLink(null), 3000);

      // Track activity
      try {
        const clientName = clients.find(c => c.id === selectedClient)?.name || selectedClient;
        const adminName = user?.attributes?.name || user?.username || 'Admin';
        ActivityTracker.addActivity('share_link',
          `${adminName} generated shareable link for "${fileName}" (${clientName})`,
          {
            fileName,
            clientId: selectedClient,
            clientName,
            admin: user?.attributes?.email
          }
        );
      } catch (trackError) {
        console.warn('Failed to track activity:', trackError);
      }

      return publicUrl;
    } catch (error) {
      console.error('Error generating public link:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        name: error.name
      });
      alert(`Failed to generate public link. Please try again.\n\nError: ${error.message}`);
      return null;
    }
  };

  const isHtmlFile = (fileName) => {
    return fileName.toLowerCase().endsWith('.html') || fileName.toLowerCase().endsWith('.htm');
  };

  const getFileIcon = (fileNameOrType) => {
    // Check if it's a file name (has extension) or mime type
    if (fileNameOrType.includes('.')) {
      const ext = fileNameOrType.split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return '🖼️';
      if (ext === 'pdf') return '📄';
      if (['doc', 'docx'].includes(ext)) return '📝';
      if (['xls', 'xlsx'].includes(ext)) return '📊';
      if (['ppt', 'pptx'].includes(ext)) return '📋';
      if (['zip', 'rar', '7z'].includes(ext)) return '🗂️';
      if (ext === 'html') return '🌐';
      return '📁';
    } else {
      // Handle mime types for upload preview
      if (fileNameOrType.startsWith('image/')) return '🖼️';
      if (fileNameOrType.includes('pdf')) return '📄';
      if (fileNameOrType.includes('word') || fileNameOrType.includes('document')) return '📝';
      if (fileNameOrType.includes('excel') || fileNameOrType.includes('spreadsheet')) return '📊';
      if (fileNameOrType.includes('powerpoint') || fileNameOrType.includes('presentation')) return '📋';
      return '📁';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'uploading': return '📤';
      case 'completed': return '✅';
      case 'error': return '❌';
      default: return '📁';
    }
  };

  return (
    <div className="admin-uploader">
      {/* Header */}
      <div className="uploader-header">
        <div className="header-content">
          <h1>File Management</h1>
          <p>Upload and manage files for your clients</p>
        </div>
        <div className="header-stats">
          {activeTab === 'upload' ? (
            <>
              <div className="stat-item">
                <span className="stat-number">{files.filter(f => f.status === 'completed').length}</span>
                <span className="stat-label">Files Uploaded</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{files.filter(f => f.status === 'pending').length}</span>
                <span className="stat-label">Pending</span>
              </div>
            </>
          ) : (
            <>
              <div className="stat-item">
                <span className="stat-number">{managedFiles.length}</span>
                <span className="stat-label">Total Files</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{selectedFilesToDelete.size}</span>
                <span className="stat-label">Selected</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          Upload Files
        </button>
        <button
          className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          Manage Files
          {location.state?.clientName && (
            <span className="client-name-tag"> - {location.state.clientName}</span>
          )}
        </button>
      </div>

      {/* Upload Configuration */}
      <div className="upload-config">
        <div className="config-row">
          <div className="config-item">
            <label htmlFor="client-select">Select Client</label>
            <select
              id="client-select"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="config-select"
            >
              <option value="">Choose a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div className="config-item">
            <label htmlFor="folder-input">Folder Name (optional)</label>
            <input
              id="folder-input"
              type="text"
              value={selectedFolder}
              onChange={(e) => setSelectedFolder(e.target.value)}
              placeholder={`Auto: ${generateFolderName()}`}
              className="config-input"
            />
          </div>
        </div>
      </div>

      {activeTab === 'upload' ? (
        <div className="upload-content">
        {/* File Drop Zone */}
        <div className="upload-section">
          <div
            className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="drop-zone-content">
              <div className="upload-icon">📤</div>
              <h3>Drop files here or click to select</h3>
              <p>Support for PDF, DOC, XLS, PPT, and image files</p>
              <input
                type="file"
                multiple
                onChange={handleFileInput}
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="file-select-btn">
                Choose Files
              </label>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="file-list">
              <div className="file-list-header">
                <h3>Selected Files ({files.length})</h3>
                <div className="file-actions">
                  <button
                    onClick={clearCompleted}
                    className="btn-secondary"
                    disabled={files.filter(f => f.status === 'completed').length === 0}
                  >
                    Clear Completed
                  </button>
                  <button
                    onClick={uploadFiles}
                    disabled={uploading || !selectedClient || files.filter(f => f.status === 'pending').length === 0}
                    className="btn-primary"
                    title={!selectedClient ? 'Please select a client first' :
                           files.filter(f => f.status === 'pending').length === 0 ? 'No files to upload' :
                           'Upload files to S3'}
                  >
                    {uploading ? 'Uploading...' :
                     !selectedClient ? 'Select Client First' :
                     'Upload Files'}
                  </button>
                </div>
              </div>

              <div className="files">
                {files.map(fileItem => (
                  <div key={fileItem.id} className={`file-item ${fileItem.status}`}>
                    <div className="file-info">
                      <span className="file-icon">
                        {getFileIcon(fileItem.type)}
                      </span>
                      <div className="file-details">
                        <span className="file-name">{fileItem.name}</span>
                        <span className="file-size">{fileItem.size}</span>
                      </div>
                    </div>

                    <div className="file-status">
                      <span className="status-icon">
                        {getStatusIcon(fileItem.status)}
                      </span>
                      {fileItem.status === 'uploading' && uploadProgress[fileItem.id] && (
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${uploadProgress[fileItem.id]}%` }}
                          />
                          <span className="progress-text">{uploadProgress[fileItem.id]}%</span>
                        </div>
                      )}
                      {fileItem.status === 'pending' && (
                        <button
                          onClick={() => removeFile(fileItem.id)}
                          className="remove-btn"
                          title="Remove file"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Upload History */}
        <div className="history-section">
          <div className="history-header">
            <h3>Recent Uploads</h3>
            <button className="view-all-btn">View All</button>
          </div>

          <div className="history-list">
            {uploadHistory.map(item => (
              <div key={item.id} className="history-item">
                <div className="history-file">
                  <span className="file-icon">📄</span>
                  <div className="file-details">
                    <span className="file-name">{item.fileName}</span>
                    <span className="file-meta">
                      {item.client} • {item.folder} • {item.size}
                    </span>
                  </div>
                </div>
                <div className="history-time">
                  {formatTimeAgo(item.uploadedAt)}
                </div>
              </div>
            ))}

            {uploadHistory.length === 0 && (
              <div className="empty-history">
                <div className="empty-icon">📤</div>
                <p>No recent uploads</p>
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (
        <div className="manage-content">
          {/* Client Selection for File Management */}
          <div className="manage-controls">
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="client-select"
            >
              <option value="">Select a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <button
              onClick={loadClientFiles}
              className="btn-primary"
              disabled={!selectedClient || loadingFiles}
            >
              {loadingFiles ? 'Loading...' : 'Load Files'}
            </button>
            {selectedFilesToDelete.size > 0 && (
              <button
                onClick={deleteSelectedFiles}
                className="btn-delete"
              >
                Delete Selected ({selectedFilesToDelete.size})
              </button>
            )}
          </div>

          {/* File List */}
          {loadingFiles && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading files...</p>
            </div>
          )}

          {!loadingFiles && managedFiles.length > 0 && (
            <div className="managed-files-list">
              <div className="files-table">
                <div className="table-header">
                  <div className="th-preview">Preview</div>
                  <div className="th-name">File Name</div>
                  <div className="th-folder">Folder</div>
                  <div className="th-size">Size</div>
                  <div className="th-actions">Actions</div>
                  <div className="th-checkbox"></div>
                </div>
                {managedFiles.map(file => {
                  console.log('Rendering file:', file.name, 'isHtml:', isHtmlFile(file.name));
                  return (
                  <div key={file.key} className="table-row">
                    <div className="td-preview">
                      {file.isImage ? (
                        <ImageThumbnail fileKey={file.key} fileName={file.name} />
                      ) : (
                        <span className="file-icon">{getFileIcon(file.name)}</span>
                      )}
                    </div>
                    <div className="td-name">
                      {file.name}
                    </div>
                    <div className="td-folder">{file.folder}</div>
                    <div className="td-size">{formatFileSize(file.size)}</div>
                    <div className="td-actions">
                      <button
                        className="btn-action btn-download"
                        onClick={() => downloadFile(file.key, file.name)}
                        title="Download file"
                      >
                        Download
                      </button>
                      {isHtmlFile(file.name) && (
                        <button
                          className={`btn-action btn-share ${copiedLink === file.key ? 'copied' : ''}`}
                          onClick={() => generatePublicLink(file.key, file.name)}
                          title="Generate shareable public link"
                        >
                          {copiedLink === file.key ? '✓ Copied!' : 'Share Link'}
                        </button>
                      )}
                    </div>
                    <div className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFilesToDelete.has(file.key)}
                        onChange={() => toggleFileSelection(file.key)}
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loadingFiles && managedFiles.length === 0 && selectedClient && (
            <div className="no-files-message">
              <p>No files found for this client</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUploader;